import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { Clock, Cpu, Zap, Smile, CheckCircle2, Monitor, ShieldCheck } from "lucide-react";

const SingleVisitDentistry = () => {
  return (
    <ServiceLayout
      title="Single Visit Dentistry (CAD/CAM)"
      subtitle="Experience the future of dentistry. Get permanent, high-quality ceramic restorations in just one appointment."
      heroImage="/images/hero/cadcam.jpg"
      images={["/images/cadcam-machine.jpg", "/images/cadcam-scan.jpg"]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <p className="text-lg text-gray-700 leading-relaxed">
          Modern dentistry has evolved significantly with the introduction of advanced digital technologies. One of the most remarkable innovations is <strong>Single Visit Dentistry</strong>, where patients can receive high-quality dental restorations in just one appointment.
        </p>
        <p className="mt-4 text-gray-700">
          At Dr. Sridhar International Dental Hospitals, we use advanced <strong>CAD CAM technology from Dentsply Sirona</strong> to design, fabricate, and place dental restorations with exceptional precision and speed.
        </p>
      </section>

      {/* Traditional vs Single Visit Comparison */}
      <section className="mb-12 grid md:grid-cols-2 gap-6">
        <div className="bg-gray-100 p-6 rounded-xl border border-gray-200">
          <h3 className="text-xl font-bold text-gray-600 mb-4 flex items-center gap-2">
            Traditional Dentistry
          </h3>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-center gap-2">✕ Multiple clinic visits</li>
            <li className="flex items-center gap-2">✕ Messy manual impressions</li>
            <li className="flex items-center gap-2">✕ Uncomfortable temporary crowns</li>
            <li className="flex items-center gap-2">✕ Weeks of waiting for the lab</li>
          </ul>
        </div>
        <div className="bg-blue-600 p-6 rounded-xl text-white shadow-lg">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            Single Visit (CAD/CAM)
          </h3>
          <ul className="space-y-3">
            <li className="flex items-center gap-2 font-medium">✓ Completed in one visit</li>
            <li className="flex items-center gap-2 font-medium">✓ Accurate digital 3D scans</li>
            <li className="flex items-center gap-2 font-medium">✓ No temporary crowns needed</li>
            <li className="flex items-center gap-2 font-medium">✓ Immediate permanent placement</li>
          </ul>
        </div>
      </section>

      {/* Tech Section */}
      <section className="bg-white border border-blue-100 p-8 rounded-2xl mb-12 shadow-sm">
        <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center gap-2">
          <Cpu className="text-blue-600" /> What is CAD CAM Technology?
        </h2>
        <p className="text-gray-700 leading-relaxed">
          CAD CAM stands for <strong>Computer-Aided Design and Computer-Aided Manufacturing</strong>. 
          In our clinic, this allows us to digitally scan your teeth, design the restoration on a computer, and then mill the restoration from high-quality ceramic blocks right within our hospital premises.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
          {[
            { icon: <Monitor className="w-5 h-5" />, label: "Digital 3D Scan" },
            { icon: <Monitor className="w-5 h-5" />, label: "Digital Design" },
            { icon: <Cpu className="w-5 h-5" />, label: "Precision Milling" },
            { icon: <Zap className="w-5 h-5" />, label: "Instant Placement" },
          ].map((step, idx) => (
            <div key={idx} className="flex flex-col items-center p-4 bg-blue-50 rounded-lg text-center">
              <div className="text-blue-600 mb-2">{step.icon}</div>
              <span className="text-xs font-bold uppercase text-blue-800">{step.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Treatments List */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Treatments Done in a Single Visit:</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "Ceramic Crowns",
            "Dental Veneers",
            "Inlays and Onlays",
            "Cosmetic Restorations",
            "Old Filling Replacements",
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-4 border border-gray-100 rounded-lg hover:border-blue-200 transition bg-gray-50/50">
              <CheckCircle2 className="text-blue-500 w-5 h-5" />
              <span className="font-semibold text-gray-700">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Advantages Ribbon */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-12 shadow-xl">
        <h2 className="text-2xl font-bold mb-8 text-center">Advantages for Patients</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="flex flex-col items-center text-center">
            <Clock className="w-10 h-10 mb-4 text-blue-300" />
            <h4 className="font-bold">Massive Time Savings</h4>
            <p className="text-sm opacity-80 mt-2">Walk in and walk out with a permanent restoration in one day.</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <ShieldCheck className="w-10 h-10 mb-4 text-blue-300" />
            <h4 className="font-bold">Digital Precision</h4>
            <p className="text-sm opacity-80 mt-2">Exceptional fit and aesthetics thanks to 3D computer design.</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <Smile className="w-10 h-10 mb-4 text-blue-300" />
            <h4 className="font-bold">Ultimate Comfort</h4>
            <p className="text-sm opacity-80 mt-2">No gooey impression materials or temporary crown sensitivity.</p>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="border-t pt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Choose Us?</h2>
        <p className="text-gray-700 mb-4">
          Dr. Sridhar International Dental Hospitals are pioneers in adopting <strong>Dentsply Sirona Digital Dentistry</strong>. 
          By integrating this technology, we provide faster, more predictable, and aesthetically pleasing results.
        </p>
        <p className="font-bold text-blue-700">
          Restore your smile quickly, comfortably, and efficiently — often in just one visit.
        </p>
      </section>
    </ServiceLayout>
  );
};

export default SingleVisitDentistry;